import {
  formatUnderscoresInField,
  getMonth,
  groupByYearMonth,
  parseYearMonthKey,
  sortYearMonthKeys
} from "."
import { PRICING_LOG_TYPE_MAP } from "../constants"
import { MonthlyPricingLogCount, PricingLogCount } from "../../types"

export const parsePricingLogTag = (tag: string | undefined): string => {
  if (!tag) return "CUSTOM QUOTE"

  const [termAndPurpose, loanTypeCode] = tag.split("_")
  const loanTerm = termAndPurpose.slice(0, -1)
  let loanPurpose: string

  if (termAndPurpose.endsWith("p")) {
    loanPurpose = "PURCHASE"
  } else if (termAndPurpose.endsWith("co")) {
    loanPurpose = "REFI CO"
  } else {
    loanPurpose = "REFI"
  }

  return `${loanTerm}YR ${loanTypeCode.toUpperCase()} ${loanPurpose}`
}

export const formatPricingLogTypeColumn = (value: string): string => {
  if (PRICING_LOG_TYPE_MAP[value]) return PRICING_LOG_TYPE_MAP[value]

  return formatUnderscoresInField(value)
}

// Helper to aggregate per-year-month
const aggregate = (data: PricingLogCount[]): Record<string, number> =>
  groupByYearMonth(
    data,
    item => item.period_start,
    item => item.pricing_log_count
  )

/**
 * Groups daily pricing log count data(success/failed/all) into monthly totals by year.
 * Includes both current and previous year if in range.
 */
export const groupPricingLogCountByMonth = (
  allPricingLogCounts: PricingLogCount[],
  successPricingLogCounts: PricingLogCount[],
  failedPricingLogCounts: PricingLogCount[]
): MonthlyPricingLogCount[] => {
  const allTotals = aggregate(allPricingLogCounts)
  const successTotals = aggregate(successPricingLogCounts)
  const failedTotals = aggregate(failedPricingLogCounts)

  // Get all unique year-month keys across all three datasets
  const uniqueKeys = new Set([
    ...Object.keys(allTotals),
    ...Object.keys(successTotals),
    ...Object.keys(failedTotals)
  ])

  // Convert to array and sort by year then month
  const sortedKeys = sortYearMonthKeys(Array.from(uniqueKeys))

  // Build final array only for months that have data
  const result: MonthlyPricingLogCount[] = sortedKeys.map(key => {
    const { year, month } = parseYearMonthKey(key)
    const monthName = getMonth(year, month)
    return {
      month: monthName,
      year: year.toString(),
      totalCount: allTotals[key] || 0,
      successCount: successTotals[key] || 0,
      failedCount: failedTotals[key] || 0
    }
  })

  return result
}
